
import React from "react";
import { Card } from "@/components/ui/card";
import { CheckCircle2, Circle } from "lucide-react";

interface ChecklistItem {
  label: string;
  isComplete: boolean;
}

interface PublicationChecklistProps {
  items: ChecklistItem[];
}

export function PublicationChecklist({ items }: PublicationChecklistProps) {
  // Filter out items that are conditional (like "This post was published...")
  const standardItems = items.filter(item => !item.label.startsWith("This post was published"));
  const publishedItem = items.find(item => item.label.startsWith("This post was published"));
  
  const completedCount = standardItems.filter(item => item.isComplete).length;
  const totalItems = standardItems.length;
  const percentComplete = Math.round((completedCount / totalItems) * 100);
  
  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4 border-b pb-4">
        <h3 className="text-lg font-medium">Publication Checklist</h3>
        <span className="text-sm font-medium bg-primary/10 text-primary px-3 py-1 rounded-full">
          {completedCount} of {totalItems} completed ({percentComplete}%)
        </span>
      </div>
      
      <div className="space-y-3 mt-4">
        {/* Standard checklist items */}
        {standardItems.map((item, index) => (
          <div key={index} className="flex items-center group">
            <div className="mr-3 text-green-500">
              {item.isComplete ? (
                <CheckCircle2 className="h-5 w-5 text-green-500" />
              ) : (
                <Circle className="h-5 w-5 text-slate-300 group-hover:text-slate-400 transition-colors" />
              )}
            </div>
            <span className={`text-sm ${item.isComplete ? 'text-slate-800 font-medium' : 'text-slate-500'}`}>
              {item.label}
            </span>
          </div>
        ))}
        
        {/* Special published date item if it exists */}
        {publishedItem && (
          <div className="flex items-center group mt-4 pt-4 border-t border-slate-100">
            <div className="mr-3 text-green-500">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
            </div>
            <span className="text-sm text-slate-800 font-medium">
              {publishedItem.label}
            </span>
          </div>
        )}
      </div>
      
      {percentComplete < 100 && !publishedItem && (
        <div className="mt-6 pt-4 border-t border-slate-100">
          <p className="text-sm text-slate-500">
            Complete all items before publishing your post for the best results.
          </p>
        </div>
      )}
    </Card>
  );
}
